/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import { useTreatments } from '@splitsoftware/splitio-react';
import { useDevice } from '@userzoomplatform/pe-study-layout';

import { useToken } from '../../hooks';
import WelcomePage from './pages/welcome-page';
import LegalPage from './pages/legal-page';
import InstallExtension from './pages/install-extension';
import { Steps, Step } from '../../components/steps';
import { Devices, Splits } from '../../utils/constants';
import Extension from './extension';
import AccessAgreementPage from './pages/access-agreement-page';

type PreStudyQueryResponse = {
	participant: {
		enableNewTasks?: boolean;
	};
	preStudy: {
		welcome: string;
		legal: {
			content: string;
			consents: string[];
		};
		accessAgreement: {
			instructions: string;
			label: string;
		} | null;
	};
	studyRequirements: {
		requiresMobileApp: boolean;
		requiresWebExtension: boolean;
	};
};

type StartStudyMutationResponse = {
	startStudy: {
		redirectUrl: string;
		url: string;
		status: string;
	};
};

const getPreStudyQuery = gql`
	query preStudy {
		preStudy {
			welcome
			legal {
				content
				consents
			}
			accessAgreement {
				instructions
				label
			}
		}
		studyRequirements {
			requiresMobileApp
			requiresWebExtension
		}
	}
`;

const getModeratedPreStudyQuery = gql`
	query preStudy {
		studyRequirements {
			requiresMobileApp
			requiresWebExtension
		}
		participant {
			enableNewTasks
		}
	}
`;

const startStudyMutation = gql`
	mutation startStudy {
		startStudy {
			redirectUrl
		}
	}
`;

const startStudyMutationNewTasks = gql`
	mutation startStudy {
		startStudy {
			redirectUrl
			status
			url
		}
	}
`;

const mobileDevices = [Devices.Mobile.toString(), Devices.Tablet.toString()];

const PreStudy: React.FC = (): ReactElement => {
	const history = useHistory();
	const treatments = useTreatments([Splits.newTasksUi]);
	const tasksUiEnabled = treatments[Splits.newTasksUi].treatment === 'on';

	const { device } = useDevice();
	const token = useToken();
	const location = useLocation();

	const isModerated = useMemo(() => {
		const params = new URLSearchParams(location.search);
		return params.get('moderated') === 'true';
	}, [location.search]);

	const preStudyQuery = isModerated ? getModeratedPreStudyQuery : getPreStudyQuery;

	const { error: preStudyError, data: preStudyData } = useQuery<PreStudyQueryResponse>(preStudyQuery);

	const [startStudy, { data: startStudyData, called: startStudyCalled }] = useMutation<StartStudyMutationResponse>(
		tasksUiEnabled ? startStudyMutationNewTasks : startStudyMutation
	);

	const preStudy = preStudyData?.preStudy;
	const hasWelcome = preStudy?.welcome ? preStudy?.welcome !== '' : false;
	const hasAccessAgreement = preStudy?.accessAgreement;
	const hasLegal = !!preStudy?.legal;
	const isMobileAppPSCL = location.pathname.includes('/mobile/pre-study');

	useEffect(
		function handleMobileAppRequirement() {
			if (preStudyError) {
				history.replace('/forbidden');
			} else if (preStudyData && device && !isMobileAppPSCL) {
				const {
					studyRequirements: { requiresMobileApp },
				} = preStudyData;
				if (mobileDevices.includes(device.type) && requiresMobileApp) {
					history.push(`/install-app?token=${token}`);
				}
			}
		},
		[device, history, preStudyData, preStudyError, token, isMobileAppPSCL]
	);

	useEffect(
		function handleStartStudy() {
			if (preStudyError) {
				history.replace('/forbidden');
			} else if (startStudyData?.startStudy.redirectUrl) {
				if (preStudyData && device && preStudyData.studyRequirements.requiresMobileApp && isMobileAppPSCL) {
					window.location.href = 'command://userzoom.com/nextState?';
				} else if (!preStudyData?.studyRequirements.requiresWebExtension) {
					window.location.href = startStudyData.startStudy.redirectUrl;
				}
			} else if (
				preStudyData &&
				!hasLegal &&
				!hasWelcome &&
				!preStudyData?.studyRequirements.requiresWebExtension &&
				!startStudyCalled &&
				!hasAccessAgreement
			) {
				startStudy();
			} else if (startStudyData?.startStudy.status && token && !preStudyData?.studyRequirements.requiresWebExtension) {
				history.replace(`/task?token=${token}`);
			} else if (startStudyData?.startStudy.url) {
				window.location.href = startStudyData?.startStudy.url;
			}
		},
		[
			preStudyError,
			hasWelcome,
			hasLegal,
			startStudyData,
			history,
			startStudy,
			preStudyData,
			device,
			isMobileAppPSCL,
			token,
			startStudyCalled,
			hasAccessAgreement,
		]
	);

	const lastStepFn = useCallback(() => {
		if (!startStudyCalled) {
			startStudy();
		}
	}, [startStudyCalled, startStudy]);

	if (!preStudy && !isModerated) {
		return <div id="no-preStudy" />;
	}

	const steps = [];

	if (hasAccessAgreement) {
		steps.push({
			key: 'accessAgreement',
			component: (
				<AccessAgreementPage label={preStudy?.accessAgreement?.label ?? ''} instructions={preStudy?.accessAgreement?.instructions ?? ''} />
			),
		});
	}

	if (hasWelcome) {
		steps.push({
			key: 'welcome',
			component: <WelcomePage content={preStudy?.welcome ?? ''} />,
		});
	}

	if (hasLegal) {
		steps.push({
			key: 'legal',
			component: <LegalPage content={preStudy?.legal.content ?? ''} consents={preStudy?.legal.consents ?? ['']} />,
		});
	}

	const requiresExtension = preStudyData && preStudyData.studyRequirements.requiresWebExtension;
	if (requiresExtension) {
		steps.push({
			key: 'installExtension',
			component: <InstallExtension />,
		});
	}

	return (
		<>
			{requiresExtension && <Extension />}
			<Steps lastStepFn={lastStepFn}>
				{steps.map(({ key, component }) => (
					<Step key={key} stepId={key} path={`${isMobileAppPSCL ? '/mobile/pre-study' : '/pre-study'}`}>
						{component}
					</Step>
				))}
			</Steps>
		</>
	);
};

export default PreStudy;
